import appStore from '../utils/state';
import gsap from 'gsap';
import type { CartState } from '../types';

export default class GiftboxModal extends HTMLElement {
  private showTl: gsap.core.Timeline | null = null;
  private hideTl: gsap.core.Timeline | null = null;

  private dialog: HTMLDialogElement | null = null;
  private closeButton: HTMLButtonElement | null = null;
  private cartItemsList: HTMLUListElement | null = null;
  private giftboxCardsContainer: HTMLElement | null = null;
  private giftboxLoyaltyInfo: HTMLElement | null = null;
  private lastReconciledRevision = 0;

  private triggerElement: HTMLElement | null = null;

  private unsubscribeFromGiftboxModalOpenState: (() => void) | null = null;
  private unsubscribeFromCartUpdated: (() => void) | null = null;

  private onGiftboxModalToggleBound = this.onGiftboxModalToggle.bind(this);
  private onCloseButtonClickBound = this.onCloseButtonClick.bind(this);
  private onDialogCancelBound = this.onDialogCancel.bind(this);
  private onDialogCloseBound = this.onDialogClose.bind(this);
  private onDialogClickBound = this.onDialogClick.bind(this);
  private onCartUpdatedBound = this.onCartUpdated.bind(this);

  connectedCallback(): void {
    this.dialog = this.querySelector('dialog');
    this.closeButton = this.querySelector('[data-giftbox-modal-ref="close-button"]');
    this.cartItemsList = this.querySelector('[data-giftbox-modal-ref="cart-items"]');
    this.giftboxCardsContainer = this.querySelector('[data-giftbox-modal-ref="giftbox-cards"]');
    this.giftboxLoyaltyInfo = this.querySelector('[data-giftbox-modal-ref="loyalty-info"]');

    this.addEventListeners();
    this.subscribe();
  }

  addEventListeners(): void {
    if (this.closeButton) {
      this.closeButton.addEventListener('click', this.onCloseButtonClickBound);
    }
    if (this.dialog) {
      this.dialog.addEventListener('cancel', this.onDialogCancelBound);
      this.dialog.addEventListener('close', this.onDialogCloseBound);
      this.dialog.addEventListener('click', this.onDialogClickBound);
    }
  }

  removeEventListeners(): void {
    if (this.closeButton) {
      this.closeButton.removeEventListener('click', this.onCloseButtonClickBound);
    }
    if (this.dialog) {
      this.dialog.removeEventListener('cancel', this.onDialogCancelBound);
      this.dialog.removeEventListener('close', this.onDialogCloseBound);
      this.dialog.removeEventListener('click', this.onDialogClickBound);
    }
  }

  subscribe(): void {
    this.unsubscribeFromGiftboxModalOpenState = appStore.subscribeKey(
      'isGiftboxModalOpen',
      this.onGiftboxModalToggleBound,
    );
    this.unsubscribeFromCartUpdated = appStore.subscribeKey('cart', this.onCartUpdatedBound);
  }

  unsubscribe(): void {
    if (this.unsubscribeFromGiftboxModalOpenState) {
      this.unsubscribeFromGiftboxModalOpenState();
    }
    if (this.unsubscribeFromCartUpdated) {
      this.unsubscribeFromCartUpdated();
      this.unsubscribeFromCartUpdated = null;
    }
  }

  onCartUpdated(cart: CartState): void {
    if (cart.revision <= this.lastReconciledRevision) return;

    const nextList = cart.data?.giftboxCartItems;
    const nextCards = cart.data?.giftboxCards;
    const nextLoyaltyInfo = cart.data?.giftboxLoyaltyInfo;

    if (!nextList && !nextCards) return;

    if (this.cartItemsList && nextList) {
      const nextHtml = nextList.innerHTML;
      const nextHtmlItems = Array.from(nextList.querySelectorAll('li'));
      const currentHtmlItems = Array.from(this.cartItemsList.querySelectorAll('li'));

      if (currentHtmlItems.length !== nextHtmlItems.length) {
        this.cartItemsList.innerHTML = nextHtml;
      }
    }

    if (this.giftboxCardsContainer && nextCards) {
      if (this.giftboxCardsContainer.innerHTML !== nextCards.innerHTML) {
        this.giftboxCardsContainer.innerHTML = nextCards.innerHTML;
      }
    }

    if (this.giftboxLoyaltyInfo && nextLoyaltyInfo) {
      if (this.giftboxLoyaltyInfo.innerHTML !== nextLoyaltyInfo.innerHTML) {
        this.giftboxLoyaltyInfo.innerHTML = nextLoyaltyInfo.innerHTML;
      }
    }

    this.lastReconciledRevision = cart.revision;
  }

  onCloseButtonClick(): void {
    appStore.actions.closeGiftboxModal();
  }

  onDialogCancel(event: Event): void {
    event.preventDefault();
    appStore.actions.closeGiftboxModal();
  }

  onDialogClick(event: MouseEvent): void {
    if (event.target === this.dialog) {
      appStore.actions.closeGiftboxModal();
    }
  }

  onDialogClose(): void {
    if (appStore.getState().isGiftboxModalOpen) {
      appStore.actions.closeGiftboxModal();
    }

    this.restoreFocus();
  }

  captureTrigger(): void {
    const trigger = document.querySelector('[aria-controls="giftbox-modal-dialog"]');

    if (trigger instanceof HTMLElement) {
      this.triggerElement = trigger;
      return;
    }

    const activeElement = document.activeElement;

    if (activeElement instanceof HTMLElement && !this.contains(activeElement)) {
      this.triggerElement = activeElement;
    }
  }

  restoreFocus(): void {
    if (this.triggerElement && document.contains(this.triggerElement)) {
      this.triggerElement.focus();
    }

    this.triggerElement = null;
  }

  onGiftboxModalToggle(isGiftboxModalOpen: boolean): void {
    if (isGiftboxModalOpen) {
      this.show();
    } else {
      this.hide();
    }
  }

  show(): void {
    if (!this.dialog) {
      return;
    }

    if (this.hideTl) {
      this.hideTl.kill();
    }
    if (this.showTl) {
      this.showTl.kill();
    }

    gsap.set(this.dialog, { autoAlpha: 0, visibility: 'visible' });

    this.captureTrigger();

    if (!this.dialog.open) {
      this.dialog.showModal();
    }

    this.showTl = gsap.timeline();
    this.showTl.fromTo(this.dialog, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.3, ease: 'power2.out' }, 0);
    this.showTl.fromTo(this.dialog, { x: '5%' }, { x: 0, force3D: true, duration: 0.7, ease: 'expo.out' }, 0);
  }

  hide(): void {
    if (!this.dialog) {
      return;
    }

    if (this.showTl) {
      this.showTl.kill();
    }
    if (this.hideTl) {
      this.hideTl.kill();
    }

    this.hideTl = gsap.timeline({
      onComplete: () => {
        if (this.dialog?.open) {
          this.dialog.close();
        }
      },
    });

    this.hideTl.to(this.dialog, { autoAlpha: 0, duration: 0.2, ease: 'power2.out' }, 0);
    this.hideTl.to(this.dialog, { x: '5%', duration: 0.3, force3D: true, ease: 'expo.out' }, 0);
  }

  disconnectedCallback(): void {
    this.removeEventListeners();
    this.unsubscribe();
  }
}

customElements.define('giftbox-modal', GiftboxModal);
